import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Empty, Link, Rule, ui } from '../ui';
import { formatDate, formatMoney } from '@goodbuild/rules/format';
import type { GiftKey, OrderSummary, SelfServeRefundOffer } from '@api/views';
import { REFUND_DAYS, REFUND_PLAYTIME_HOURS } from '@goodbuild/rules/refund-policy';
import { ApiError, useApi, useApiMutation } from '../api';
import { Loading, Notice, Screen, Table } from './shared';

/**
 * Receipts, and the refund clause 14.3 promises.
 *
 * The refunded total is summed from the refund rows by the API, never read
 * from a column: hard rule 2 forbids a stored running total anywhere, and a
 * receipt is exactly the place somebody would notice it drifting.
 */
export function OrdersRoute(): React.ReactElement {
  const query = useApi<{ orders: OrderSummary[] }>(['orders'], '/me/orders');

  return (
    <Screen title="Orders" query={query}>
      {query.data && query.data.orders.length === 0 ? (
        <Empty>
          Nothing bought yet. <Link href={'/'}>Look at our games</Link>.
        </Empty>
      ) : (
        <div className={ui.stack ?? ''}>
          <Gifts />
          <Refunds />

          <Table head={['Date', 'Games', 'Paid', 'Tax', 'Refunded', 'Status']}>
            {query.data?.orders.map(({ order, lines, refundedMinor }) => (
              <tr key={order.id}>
                <td className={ui.numeric ?? ''}>{formatDate(order.placedAt)}</td>
                <td>
                  {lines.map((line) => (
                    <div key={line.id}>
                      <Link href={`/g/${line.slug}`}>{line.name}</Link>
                    </div>
                  ))}
                </td>
                <td className={ui.numeric ?? ''}>
                  {formatMoney(order.currency, order.pricePaidMinor)}
                </td>
                <td className={ui.numeric ?? ''}>{formatMoney(order.currency, order.taxMinor)}</td>
                <td className={ui.numeric ?? ''}>
                  {refundedMinor > 0 ? formatMoney(order.currency, refundedMinor) : '—'}
                </td>
                <td>{order.state.replace(/_/g, ' ')}</td>
              </tr>
            ))}
          </Table>
        </div>
      )}
    </Screen>
  );
}

/**
 * The keys for games bought as presents (#74).
 *
 * **The key is the whole deliverable, so it is shown here in full, every
 * time.** A gift goes to an email address rather than to an account — the buyer
 * usually does not know whether their friend has ever been here — so what they
 * are given is a key to pass on, and the only sensible answer to "I deleted the
 * message" is to show it again. Nothing is protected by hiding it: the merchant
 * of record puts the same key on its confirmation page, in its receipt email
 * and in its own customer portal. What protects a gift is that redeeming one
 * grants exactly one entitlement, once.
 *
 * **A copy button is not a substitute for the text.** The key gets pasted into
 * a birthday message, read down a phone, or written on a card, so it is
 * rendered as selectable text and left there.
 *
 * *Claimed* is shown without saying by whom. The buyer wants to know whether it
 * landed; which account it landed on would turn an address they typed into a
 * way of asking whether that person has a GOODBUILD account, which is the
 * disclosure hard rule 20 exists to prevent.
 *
 * Absent entirely when nobody has bought a gift, for the reason `Refunds` is:
 * an empty *Gifts* heading over a dashed box reads as something broken.
 */
function Gifts(): React.ReactElement | null {
  const query = useApi<{ gifts: GiftKey[] }>(['gifts'], '/gifts');
  const keys = query.data?.gifts ?? [];

  if (query.isPending) return <Loading label="Looking for gift keys" />;
  // A failure costs the section rather than the page — the receipts below are
  // why somebody opened this screen and they came back fine.
  if (query.error) return <Notice>Gift keys could not be loaded just now.</Notice>;
  if (keys.length === 0) return null;

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Gifts you bought</h2>

      <p className="gb-body">
        Send the key to whoever the game is for. They type it in on their Library page and the game
        is theirs. Keep the key — it is the only way to claim the game, and this page always shows
        it again.
      </p>

      <div className={ui.stack ?? ''}>
        {keys.map((gift) => (
          <div key={gift.licenceId} className={ui.panel ?? ''}>
            <p className="gb-body">
              <strong>{gift.productName}</strong>
              {gift.giftEmail ? ` — for ${gift.giftEmail}` : null}
              {' — '}
              {formatDate(gift.boughtAt)}
            </p>

            {gift.refunded ? (
              <Notice>This one was refunded, so the key does not work any more.</Notice>
            ) : gift.redeemed ? (
              <p className="gb-body">
                Claimed{gift.redeemedAt ? ` ${formatDate(gift.redeemedAt)}` : ''}.
              </p>
            ) : (
              <p className={`gb-body ${ui.numeric ?? ''}`}>{gift.key}</p>
            )}
          </div>
        ))}
      </div>

      <Rule />
    </>
  );
}

/**
 * Clause 14.3: under two hours played, inside fourteen days, no questions.
 *
 * **A section of its own rather than a column in the table**, and that is about
 * what the two things are. The table is a record of what happened, one row per
 * order, going back forever; this is a small set of things somebody can still
 * *do*, which is at most a fortnight's worth. Squeezing it into a cell would
 * mean either a button with no explanation beside it or a paragraph inside a
 * numeric table, and the paragraph is the important half — "you have played it
 * for three hours" is the whole answer, and a greyed-out button says none of
 * it.
 *
 * **Every refusal is a sentence, and it comes from the server.** The API
 * returns `RefundRefusal[]` carrying text written for a person to read, for the
 * reason `PurchaseProblem` does: a client that turned `too_much_played` into its
 * own wording would be the second place that policy is described, and the two
 * would disagree the first time a threshold moved. Nothing here decides
 * anything — it renders what came back.
 *
 * Absent entirely when there is nothing in the window. An empty *Refunds*
 * heading over a dashed box reads as something broken; a receipts page with no
 * refund section reads as a receipts page.
 */
function Refunds(): React.ReactElement | null {
  const key = ['refunds'] as const;
  const query = useApi<{ offers: SelfServeRefundOffer[] }>(key, '/me/refunds');
  const call = useApiMutation();
  const cache = useQueryClient();

  const [busy, setBusy] = useState<string | null>(null);
  const [asked, setAsked] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const offers = query.data?.offers ?? [];

  async function refund(offer: SelfServeRefundOffer): Promise<void> {
    setBusy(offer.orderId);
    setMessage(null);
    setFailure(null);

    try {
      await call(`/me/orders/${offer.orderId}/refund`, { method: 'POST' });
      setMessage(
        'Asked. A person will look at it and answer within three working days. ' +
          `Nothing has left your library, and you can keep playing ${
            offer.titles.length === 1 ? 'it' : 'them'
          } while you wait.`,
      );
      setAsked(null);
      /*
       * Three caches, because a refund changes three screens.
       *
       * The receipts row's state and refunded total, this section's answer for
       * that order — which becomes `already_refunded` — and the library, which
       * has just lost a game. Leaving the library stale is the one that would
       * be noticed: somebody refunds, clicks Library, and the game they were
       * just told was removed is still sitting there.
       */
      await Promise.all([
        cache.invalidateQueries({ queryKey: key }),
        cache.invalidateQueries({ queryKey: ['orders'] }),
        cache.invalidateQueries({ queryKey: ['library'] }),
      ]);
    } catch (error) {
      // Core's refusals carry a sentence written for a person, and the API maps
      // them to a status with that sentence intact. Showing it beats replacing
      // it with something vaguer on the one screen where the reason is the
      // whole point.
      setFailure(error instanceof ApiError ? error.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  }

  if (query.isPending) return <Loading label="Checking what can be refunded" />;

  // A failure here costs a section rather than the page: the receipts below are
  // the reason somebody opened this screen, and they came back fine.
  if (query.error) return <Notice>Refunds could not be checked just now.</Notice>;
  if (offers.length === 0) return null;

  return (
    <>
      <h2 className={ui.sectionTitle ?? ''}>Refunds</h2>

      <p className="gb-body">
        Played under {REFUND_PLAYTIME_HOURS} hours, and bought in the last {REFUND_DAYS} days? Ask
        here. A person reads every request and answers within three working days. We count real
        playing time, not how long the game was open.
      </p>

      {message ? (
        <p className="gb-body" role="status">
          {message}
        </p>
      ) : null}

      {failure ? <Notice alert>{failure}</Notice> : null}

      <div className={ui.stack ?? ''}>
        {offers.map((offer) => (
          <div key={offer.orderId} className={ui.panel ?? ''}>
            <p className="gb-body">
              <strong>{offer.titles.map((title) => title.productName).join(', ')}</strong>
              {' — '}
              {formatDate(offer.placedAt)}
              {' — '}
              <span className={ui.numeric ?? ''}>
                {formatMoney(offer.currency, offer.amountMinor)}
              </span>
            </p>

            {offer.decision.eligible ? (
              /*
                Two presses rather than one, the same shape as signing out.

                It asks rather than refunds now — an administrator decides, see
                `lib/orders/refund-requests.ts` — but the second press stays.
                Approval revokes the game, and it is once per title, so a stray
                click still starts something somebody did not mean to start.
              */
              asked === offer.orderId ? (
                <div className={ui.actions ?? ''}>
                  <button
                    type="button"
                    className={ui.button ?? ''}
                    disabled={busy === offer.orderId}
                    onClick={() => void refund(offer)}
                  >
                    {busy === offer.orderId
                      ? 'Sending…'
                      : `Ask for ${formatMoney(offer.currency, offer.amountMinor)} back`}
                  </button>
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    disabled={busy === offer.orderId}
                    onClick={() => setAsked(null)}
                  >
                    Keep it
                  </button>
                </div>
              ) : (
                <div className={ui.actions ?? ''}>
                  <button
                    type="button"
                    className={ui.buttonSecondary ?? ''}
                    onClick={() => setAsked(offer.orderId)}
                  >
                    Ask for a refund
                  </button>
                  <span className="gb-body">
                    If it is granted, {offer.titles.length === 1 ? 'the game' : 'the games'} will be
                    removed from your library.
                  </span>
                </div>
              )
            ) : (
              offer.decision.refusals.map((refusal) => (
                <p className="gb-body" key={`${refusal.code}:${refusal.productSlug ?? ''}`}>
                  {refusal.message}
                </p>
              ))
            )}
          </div>
        ))}
      </div>

      <Rule />
    </>
  );
}
